// Legacy vendor-prefixed IndexedDB globals not present in the standard lib.dom types.
type IndexedDBWindow = typeof window & {
    mozIndexedDB?: IDBFactory;
    webkitIndexedDB?: IDBFactory;
    msIndexedDB?: IDBFactory;
    shimIndexedDB?: IDBFactory;
};

export class IndexDB {
    id = 'Builder';
    storeID = 'Store';
    initPromise: Promise<IDBDatabase> | null = null;
    db: IDBDatabase | null = null;
    store: IDBObjectStore | null = null;
    tx: IDBTransaction | null = null;

    constructor() {
        this.init();
    }

    init(): Promise<IDBDatabase> {
        if (!this.initPromise) {
            this.initPromise = new Promise<IDBDatabase>((resolve) => {
                // This works on all devices/browsers, and uses IndexedDBShim as a final fallback
                const w = window as IndexedDBWindow;
                const indexedDB =
                    w.indexedDB || w.mozIndexedDB || w.webkitIndexedDB || w.msIndexedDB || w.shimIndexedDB;

                // Open (or create) the database
                const open = indexedDB.open(this.id, 1);

                // Create the schema
                open.onupgradeneeded = () => {
                    this.db = open.result;
                    this.db.createObjectStore(this.storeID, { keyPath: 'id' });
                    // var index = store.createIndex('NameIndex', ['name.last', 'name.first']);
                };

                open.onsuccess = () => {
                    this.db = open.result;
                    resolve(open.result);
                };
            });
        }

        return this.initPromise;
    }

    close(): void {
        this.db && this.db.close();
        this.db = null;
        this.store = null;
        this.tx = null;
        this.initPromise = null;
    }

    getStore = async (): Promise<{ store: IDBObjectStore; tx: IDBTransaction }> => {
        const db = await this.init();
        const tx = db.transaction(this.storeID, 'readwrite');
        const store = tx.objectStore(this.storeID);
        return { store, tx };
    };

    getById = async (id: IDBValidKey): Promise<unknown> => {
        const { store } = await this.getStore();
        return new Promise((resolve, reject) => {
            const getID = store.get(id);
            getID.onsuccess = () => resolve(getID.result);
            getID.onerror = () => reject(getID.error);
        });
    };

    put = async (data: unknown): Promise<void> => {
        const { store, tx } = await this.getStore();
        store.put(data);
        return new Promise((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    };
}

const DB = new IndexDB();
DB.init();

export default DB;
